<script setup>
// ---------------------------------------------------------------------------
// What share of the market the Office actually sees, as two rings.
//
// The whole ring is always the whole market for the period — it never rescales.
// The filter only changes how much of it is coloured in. That is the difference
// from a bar whose length is a fraction: here the reader can see the two percent
// of contracts and the third of the money against the same fixed circle, and
// ticking a year or a sector lights up less of it rather than redrawing it.
//
// The market totals are not in this dataset and never can be: they cover every
// contract awarded under the act. They come from market.json, one row per year
// and sector, so picking a year or a sector narrows the whole ring as well. Only
// the court filter cannot narrow it — the market has no such column — and the
// note under the rings says so when that happens.
//
// The money ring is split rather than solid: the same share, told as how far the
// contracts behind it got. A flat fifth said the Office touches a fifth of the
// money; the split says two thirds of that fifth was appealed, which is the
// sentence the chapter is actually about.
// ---------------------------------------------------------------------------

import { computed } from 'vue'

import {
    MARKET,
    marketFate,
    money,
    narrowContracts,
    outsideFilters,
    sectorVolume,
    unpackFilter
} from '../cases.js'
import { filters } from '../store.js'
import { useContracts, useJson } from '../useCases.js'

const BILLION = 1e9

// A ring drawn by dashing its own circumference: no arc maths, no shape library.
const R = 60
const CIRCUMFERENCE = 2 * Math.PI * R

const props = defineProps({
    src: { type: String, required: true },
    marketSrc: { type: String, required: true },
    countLabel: { type: String, default: '' },
    volumeLabel: { type: String, default: '' },
    countNote: { type: String, default: '' },   // "{part} z {total} zakázek"
    volumeNote: { type: String, default: '' },
    // The two wedges the money ring is made of.
    firstLabel: { type: String, default: '' },
    secondLabel: { type: String, default: '' },
    // Pipe-separated: billions, millions, thousands.
    units: { type: String, default: '' },
    // "u {pocet} zakázek chybí cena"
    missingNote: { type: String, default: '' },
    emptyText: { type: String, default: '' },
    errorText: { type: String, default: '' }
})

const { rows, failed } = useContracts(props.src)
const { data: table, failed: marketFailed } = useJson(props.marketSrc, { rows: [] })

// Not `visible`: the ring is a share of the market, so both sides narrow by
// year and sector and by nothing else. Orders stay in — the market total counts
// every contract awarded, so dropping them here would shrink the numerator
// against a denominator that kept them.
const mine = computed(() => narrowContracts(rows.value, outsideFilters(filters)))

const data = computed(() => (rows.value.length ? sectorVolume(mine.value) : null))

// The market table is [rok, obor, počet, objem]; the same two dimensions the
// filter offers, minus the courts.
const market = computed(() => {
    const years = unpackFilter(filters.rok)
    const sectors = unpackFilter(filters.obor)

    const wanted = (table.value.rows ?? []).filter(
        ([year, sector]) =>
            (!years.length || years.includes(year)) && (!sectors.length || sectors.includes(sector))
    )

    return {
        contracts: wanted.reduce((total, row) => total + row[MARKET.contracts], 0),
        billions: wanted.reduce((total, row) => total + row[MARKET.volume], 0) / BILLION
    }
})

// Before the market file lands there is no denominator either, and that is not
// the same thing as the filter having narrowed it away.
const loaded = computed(() => Boolean((table.value.rows ?? []).length))

const fate = computed(() => marketFate(mine.value, table.value.rows, filters))

// How far the money got, as two wedges laid end to end around the same ring.
const wedges = computed(() => {
    const whole = fate.value.whole || 1
    let offset = 0

    return [
        { key: 'first', label: props.firstLabel, value: fate.value.first },
        { key: 'second', label: props.secondLabel, value: fate.value.second }
    ].map((wedge) => {
        const length = (wedge.value / whole) * CIRCUMFERENCE
        const drawn = { ...wedge, dash: `${length} ${CIRCUMFERENCE - length}`, offset: -offset }

        offset += length

        return drawn
    })
})

const rings = computed(() => [
    {
        key: 'count',
        label: props.countLabel,
        part: data.value.contracts,
        total: market.value.contracts,
        note: props.countNote
    },
    {
        key: 'volume',
        label: props.volumeLabel,
        part: data.value.total / BILLION,
        total: market.value.billions,
        note: props.volumeNote,
        wedges: wedges.value
    }
].map((ring) => {
    const share = ring.total ? ring.part / ring.total : 0

    return {
        ...ring,
        share,
        dash: `${share * CIRCUMFERENCE} ${CIRCUMFERENCE}`,
        text: ring.note.replace('{part}', number(ring.part)).replace('{total}', number(ring.total))
    }
}))

const missing = computed(() =>
    props.missingNote.replace('{pocet}', number(fate.value.missingPrice))
)

const billions = (value) => money(value, props.units.split('|'))

const number = (value) => value.toLocaleString('cs-CZ', { maximumFractionDigits: 0 })
const percent = (value) =>
    value.toLocaleString('cs-CZ', { style: 'percent', maximumFractionDigits: 1 })
</script>

<template>
    <p v-if="failed || marketFailed" class="chart__failed">{{ errorText }}</p>

    <!-- A share needs something to be a share of. Narrow the filter to a year
         and a sector the market table has no row for and there is no
         denominator, which is a thing to say rather than an empty space. -->
    <p v-else-if="loaded && !market.contracts" class="chart__failed">{{ emptyText }}</p>

    <div v-else-if="data && market.contracts">
        <div class="rings">
            <figure v-for="ring in rings" :key="ring.key" class="rings__item statbox statbox--panel">
                <svg class="rings__plot" viewBox="0 0 160 160" role="img"
                     :aria-label="`${ring.label}: ${percent(ring.share)}`">
                    <g transform="translate(80 80) rotate(-90)">
                        <circle class="rings__track" :r="R" />

                        <!-- One arc when the ring is a plain share, two when it
                             also says how far the money got. -->
                        <template v-if="ring.wedges">
                            <circle
                                v-for="wedge in ring.wedges"
                                :key="wedge.key"
                                class="rings__part"
                                :class="`rings__part--${wedge.key}`"
                                :r="R"
                                :stroke-dasharray="wedge.dash"
                                :stroke-dashoffset="wedge.offset"
                            />
                        </template>

                        <circle v-else class="rings__part" :r="R" :stroke-dasharray="ring.dash" />
                    </g>
                    <text class="rings__value" x="80" y="80" text-anchor="middle"
                          dominant-baseline="central">{{ percent(ring.share) }}</text>
                </svg>

                <figcaption>
                    <p class="rings__label">{{ ring.label }}</p>
                    <p class="rings__note">{{ ring.text }}</p>

                    <dl v-if="ring.wedges" class="rings__split">
                        <div v-for="wedge in ring.wedges" :key="wedge.key" class="rings__wedge">
                            <dt>
                                <i :class="`rings__key rings__key--${wedge.key}`" aria-hidden="true"></i>
                                {{ wedge.label }}
                            </dt>
                            <dd>{{ billions(wedge.value) }}</dd>
                        </div>
                    </dl>
                </figcaption>
            </figure>
        </div>

        <p class="note">{{ missing }}</p>
    </div>
</template>
